import { Outlet, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { House, Calendar, Users, Receipt, Bell, SignOut, Wallet, Robot, UsersThree, UsersFour, CurrencyDollar, Ruler, ChartLineUp, Package, Gear, EyeSlash, Camera, Barbell, ClipboardText } from "@phosphor-icons/react";
import { useAuth } from "../../context/AuthContext";
import { usePrefs } from "../../context/PrefsContext";
import { useBrand } from "../../context/BrandContext";
import Logo from "../../components/Logo";
import PrefsToggle from "../../components/PrefsToggle";
import TimezoneBanner from "../../components/TimezoneBanner";
import NotificationBell from "../../components/NotificationBell";
import TrainerAssistantBubble from "../../components/TrainerAssistantBubble";
import { SupportBubble } from "../../components/SupportBubble";
import { StripeSetupBanner } from "../../components/StripeSetupBanner";
import { GetAppButton } from "../../components/InstallGuide";
import { PlanGate } from "../../components/PlanGate";
import api from "../../lib/api";

export default function TrainerLayout() {
  const { user, logout } = useAuth();
  const { privacy } = usePrefs();
  const brand = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadReplies, setUnreadReplies] = useState(0);
  const [pendingCheckins, setPendingCheckins] = useState(0);
  const [subStatus, setSubStatus] = useState(null);

  const fetchSubStatus = async (refresh = false) => {
    try {
      const { data } = await api.get(`/platform/billing/status${refresh ? "?refresh=true" : ""}`);
      setSubStatus(data);
      return data;
    } catch (_e) {
      setSubStatus({ required: false });
      return null;
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cameFromCheckout = params.has("sub_session") || params.has("addon_session");
    fetchSubStatus(cameFromCheckout).then(() => {
      if (params.has("sub_session")) {
        params.delete("sub_session");
        window.history.replaceState({}, "", window.location.pathname);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let alive = true;
    const fetchBadge = async () => {
      try {
        const { data } = await api.get("/dashboard/unread-progress-replies");
        if (alive) setUnreadReplies(data?.count || 0);
      } catch (_e) {
        if (alive) setUnreadReplies(0);
      }
      try {
        const { data } = await api.get("/checkins/pending-count");
        if (alive) setPendingCheckins(data?.count || 0);
      } catch (_e) {
        if (alive) setPendingCheckins(0);
      }
    };
    fetchBadge();
    // Re-fetch on route change AND every 60s
    const id = setInterval(fetchBadge, 60_000);
    return () => { alive = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const gated = subStatus && subStatus.required && !subStatus.active;
  const groupOnly = Boolean(gated && subStatus.group_active);

  const allItems = [
    { to: "/trainer", end: true, icon: <House size={18} weight="duotone" />, label: "Dashboard" },
    { to: "/trainer/schedule", icon: <Calendar size={18} weight="duotone" />, label: "Calendar" },
    { to: "/trainer/group", icon: <UsersFour size={18} weight="duotone" />, label: "Group" },
    { to: "/trainer/clients", icon: <Users size={18} weight="duotone" />, label: "Clients" },
    { to: "/trainer/progress", icon: <Camera size={18} weight="duotone" />, label: "Progress", badge: unreadReplies },
    { to: "/trainer/coaching", icon: <ClipboardText size={18} weight="duotone" />, label: "Coaching", badge: pendingCheckins },
    { to: "/trainer/metrics", icon: <Ruler size={18} weight="duotone" />, label: "Metrics" },
    { to: "/trainer/assessment", icon: <Barbell size={18} weight="duotone" />, label: "Assessment" },
    { to: "/trainer/analytics", icon: <ChartLineUp size={18} weight="duotone" />, label: "Analytics" },
    { to: "/trainer/invoices", icon: <Receipt size={18} weight="duotone" />, label: "Invoices" },
    { to: "/trainer/packages", icon: <Package size={18} weight="duotone" />, label: "Packages" },
    { to: "/trainer/earnings", icon: <CurrencyDollar size={18} weight="duotone" />, label: "Earnings" },
    { to: "/trainer/payroll", icon: <Wallet size={18} weight="duotone" />, label: "Payroll" },
    { to: "/trainer/bot", icon: <Robot size={18} weight="duotone" />, label: "AI Bot" },
    { to: "/trainer/notifications", icon: <Bell size={18} weight="duotone" />, label: "Notifications" },
    ...(user?.is_owner ? [{ to: "/trainer/team", icon: <UsersThree size={18} weight="duotone" />, label: "Team" }] : []),
    { to: "/trainer/settings", icon: <Gear size={18} weight="duotone" />, label: "Settings" },
  ];
  const items = groupOnly
    ? allItems.filter((it) => it.to === "/trainer/group")
    : user?.business_type === "group"
      ? [allItems.find((it) => it.to === "/trainer/group"), ...allItems.filter((it) => it.to !== "/trainer/group")]
      : allItems;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="trainer-layout">
      {/* Top bar */}
      <header className="border-b border-white/10 sticky top-0 z-40 bg-[#0A0A0A]/95 backdrop-blur">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {brand.brand_logo_b64 ? (
              <img src={brand.brand_logo_b64} alt={(brand.brand_name || "logo") + " logo"} className="h-7 w-7 object-contain" data-testid="trainer-brand-logo" />
            ) : (
              <Logo size={28} />
            )}
            <div className="min-w-0">
              <div className="font-display text-xl tracking-tight leading-none uppercase" data-testid="trainer-brand-name">
                {brand.brand_name || "TRAINA"}
              </div>
              <div className="tracker text-zinc-500 mt-1 hidden md:block">
                {brand.brand_name ? "Your coaching command center" : "Run your training business with confidence"}
              </div>
            </div>
            <span className="tracker ml-2 px-2 py-1 border border-white/10 hidden sm:inline-block" data-testid="role-badge">{user?.is_owner ? "Owner" : "Trainer"}</span>
            {privacy && (
              <span
                className="tracker px-2 py-1 border border-brand/50 bg-brand/10 text-brand-light hidden sm:inline-flex items-center gap-1.5"
                data-testid="privacy-badge"
                title="Privacy mode — financial figures are blurred"
              >
                <EyeSlash size={11} weight="bold" />
                Privacy on
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <PrefsToggle />
            <NotificationBell />
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold">{user?.name}</div>
              <div className="tracker">{user?.email}</div>
            </div>
            <button onClick={handleLogout} className="btn-ghost !py-2 !px-3" data-testid="trainer-logout">
              <SignOut size={16} weight="bold" />
            </button>
          </div>
        </div>
      </header>

      <TimezoneBanner />
      {!gated && <StripeSetupBanner />}

      {gated && !groupOnly ? (
        <PlanGate status={subStatus} onRefresh={() => fetchSubStatus(true)} />
      ) : (
      <div className="max-w-[1600px] mx-auto grid lg:grid-cols-[220px_1fr]">
        {/* Side nav */}
        <aside className="border-r border-white/10 min-h-[calc(100vh-65px)] hidden lg:block">
          <nav className="p-4 space-y-1">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 tracker hover:bg-[#141414] ${
                    isActive ? "bg-[#141414] text-white border-l-2 border-brand" : "text-zinc-400"
                  }`
                }
                data-testid={`trainer-nav-${it.label.toLowerCase()}`}
              >
                {it.icon} {it.label}
                {it.badge > 0 && (
                  <span
                    className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold bg-emerald-500 text-zinc-950 rounded-full"
                    data-testid={`trainer-nav-badge-${it.label.toLowerCase()}`}
                  >
                    {it.badge > 99 ? "99+" : it.badge}
                  </span>
                )}
              </NavLink>
            ))}
            <div className="pt-2 mt-2 border-t border-white/10">
              <GetAppButton />
            </div>
          </nav>
        </aside>

        {/* Mobile nav */}
        <div className="lg:hidden border-b border-white/10 overflow-x-auto min-w-0">
          <div className="flex">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                className={({ isActive }) =>
                  `relative flex items-center gap-2 px-4 py-3 tracker whitespace-nowrap ${
                    isActive ? "text-white border-b-2 border-brand" : "text-zinc-400"
                  }`
                }
              >
                {it.icon} {it.label}
                {it.badge > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[9px] font-bold bg-emerald-500 text-zinc-950 rounded-full" data-testid={`trainer-nav-mobile-badge-${it.label.toLowerCase()}`}>
                    {it.badge > 99 ? "99+" : it.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        </div>

        <main className="p-4 sm:p-6 lg:p-10 min-w-0">
          {groupOnly && !location.pathname.startsWith("/trainer/group") ? (
            <Navigate to="/trainer/group" replace />
          ) : (
            <Outlet />
          )}
        </main>
      </div>
      )}

      {/* Iter 60.1 — floating AI assistant bubble, available on every trainer page */}
      <TrainerAssistantBubble />
      <SupportBubble />
    </div>
  );
}
